import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LoanListItem, GroupMember } from '@app/shared';
import {
  PACKAGE_META, ALLOWED_PACKAGES_BY_PHASE,
  UPP_STATUSES, UPP_CO_NOT_REQUESTED_REASONS,
  PURCHASE_REASONS, EXTENSION_REASONS, capturesStatusAtCreate, UPP_WITHOUT_CO, carriesChargeOff,
  ETRAN_LOAN_STATUSES, SBA_LOAN_STATUSES,
  ROLE_ADMIN, ROLE_LOAN_ANALYST, ROLE_SPECIAL_ASSETS,
  type PackageType, type LoanPhase,
} from '@app/shared';
import { cachedLoanList, listLoans, loanListCacheKey, restoreLoan, createRequest } from '../api';
import { useAuth } from '../auth/AuthProvider';
import { listMembersForRole } from '../graph';
import { buildPackageFieldsFromRaw, litTotalFeesDisplay } from '../pkgFieldsUtil';
import { ANALYST_COLUMN_IDS, SA_COLUMN_IDS } from '../table/columns';
import { defaultFiltersFor } from '../table/defaultFilters';
import { FilterBar } from '../table/FilterBar';
import { FindBar } from '../table/FindBar';
import { LoansTable } from '../table/LoansTable';
import { useTableView } from '../table/useTableView';
import { patchViewState, readViewState } from '../table/viewState';
import { type SortSpec } from '../table/filterModel';

// Rows rendered per page. Client-side filter/sort still runs over the full
// returned set; only this many rows are painted at once, which keeps both
// instant even with several thousand loans loaded.
const PAGE_SIZE = 100;

// Name this grid's remembered view (filters, ordering, page, expanded rows) is
// stored under, so clicking into a loan and back doesn't discard it. One key for
// both role variants: a chip naming a column the other role doesn't show is
// dropped on read, which is the same thing keying by role would achieve.
const VIEW_KEY = 'loans';

// Stable identity for the not-yet-loaded case, so the view memo doesn't
// recompute on every render while the fetch is in flight.
const EMPTY_ROWS: LoanListItem[] = [];

// Both grids open as a work queue: soonest Next Action first, overdue floating
// to the top, undated last (compareBy puts empties last on its own). Module
// constants so their identity is stable across renders — a fresh object here
// would invalidate the view memo and re-sort on every keystroke.
const DEFAULT_SORT: SortSpec = { id: 'nextActionDate', dir: 'asc' };
// Two loans due the same day read better alphabetically than arbitrarily, and a
// stable secondary key stops equal rows shuffling between orderings.
const SORT_TIEBREAK = (a: LoanListItem, b: LoanListItem) =>
  a.borrowerName.localeCompare(b.borrowerName);

// The package types this user may initiate, given the loan's current phase.
function pkgTypesForUser(phase: LoanPhase, isAdmin: boolean, isSa: boolean, isAnalyst: boolean): PackageType[] {
  const allowed = ALLOWED_PACKAGES_BY_PHASE[phase];
  if (isAdmin) return allowed;
  return allowed.filter(t => {
    const initiator = PACKAGE_META[t].initiator;
    return (isSa && initiator === 'special_assets') || (isAnalyst && initiator === 'loan_analyst');
  });
}

// Default package type based on loan phase: Purchase for Prepurchase loans,
// otherwise no default so the user must pick.
function defaultPackageType(phase: LoanPhase): PackageType | '' {
  return phase === 'Prepurchase' ? 'Purchase' : '';
}

type PkgDetails = Record<string, string | boolean>;

function emptyDetails(): PkgDetails { return {}; }

export function LoansPage() {
  const { hasRole, me } = useAuth();
  const navigate = useNavigate();
  const isAdmin = hasRole(ROLE_ADMIN);
  const isAnalyst = hasRole(ROLE_LOAN_ANALYST);
  const isSa = hasRole(ROLE_SPECIAL_ASSETS);
  const canNewPkg = isAdmin || isSa || isAnalyst;
  const canDelete = isAdmin;
  const saOnly = hasRole(ROLE_SPECIAL_ASSETS) && !isAdmin && !hasRole(ROLE_LOAN_ANALYST);

  const [loans, setLoans] = useState<LoanListItem[] | null>(null);
  const [saOfficers, setSaOfficers] = useState<GroupMember[]>([]);
  const [analysts, setAnalysts] = useState<GroupMember[]>([]);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  // Page and expanded rows are restored from the same stored view the table uses
  // for its filters, so coming back from a loan lands on the rows you left — not
  // just the right filters applied to page 1. Read lazily: once per mount, before
  // first paint. `col` isn't consulted for either field, so no column lookup is
  // needed here.
  const [page, setPage] = useState(() => readViewState(VIEW_KEY, () => undefined).page ?? 1);
  const [expanded, setExpanded] = useState<Set<number>>(
    () => new Set(readViewState(VIEW_KEY, () => undefined).expanded ?? []),
  );
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // "New Package" modal state
  const [pkgLoan, setPkgLoan] = useState<LoanListItem | null>(null);
  const [pkgType, setPkgType] = useState<PackageType | ''>('');
  const [pkgSaId, setPkgSaId] = useState('');
  const [pkgAnalystId, setPkgAnalystId] = useState('');
  const [pkgEtranStatus, setPkgEtranStatus] = useState('');
  const [pkgSbaStatus, setPkgSbaStatus] = useState('');
  const [pkgComments, setPkgComments] = useState('');
  const [pkgDetails, setPkgDetails] = useState<PkgDetails>(emptyDetails());
  const [pkgSubmitting, setPkgSubmitting] = useState(false);
  const [pkgError, setPkgError] = useState<string | null>(null);

  // Every role now loads the same set — the whole portfolio they're permitted
  // to see — and narrows it with filters. Scoping to "my assignments" moved out
  // of the query and into a default chip, which is what lets a user drop it and
  // look at a colleague's queue or at unassigned loans.
  //
  // Stale-while-revalidate: paint whatever the last visit fetched, then refetch
  // and replace it. Returning from a loan detail is the common case and it now
  // costs no wait, while the list still converges on what other people have
  // changed — which a cache with an expiry could not promise either way.
  const load = async () => {
    setError(null);
    const wantsDeleted = includeDeleted && isAdmin;
    const cached = cachedLoanList(loanListCacheKey(wantsDeleted));
    if (cached) setLoans(cached);
    try {
      setLoans(await listLoans({ includeDeleted: wantsDeleted }));
    } catch (e) {
      // Note what failed but keep the rows already on screen. Stale rows plus an
      // error beat an empty grid; only a first load with nothing painted should
      // leave the user looking at the empty state.
      setError(String(e));
    }
  };

  useEffect(() => {
    void load();
  }, [includeDeleted]);

  useEffect(() => {
    if (canNewPkg) {
      const onErr = (e: unknown) => { console.error('[members] failed to load group members', e); setMemberError(String(e)); };
      void listMembersForRole('special_assets').then(setSaOfficers).catch(onErr);
      void listMembersForRole('loan_analyst').then(setAnalysts).catch(onErr);
    }
  }, []);

  const pkgTypes = pkgLoan ? pkgTypesForUser(pkgLoan.phase, isAdmin, isSa, isAnalyst) : [];

  const openPkgModal = (loan: LoanListItem) => {
    setPkgLoan(loan);
    const types = pkgTypesForUser(loan.phase, isAdmin, isSa, isAnalyst);
    const def = defaultPackageType(loan.phase);
    setPkgType(types.includes(def as PackageType) ? def : '');
    setPkgSaId(me?.oid ?? '');
    setPkgAnalystId(me?.oid ?? '');
    setPkgEtranStatus(loan.etranLoanStatus ?? '');
    setPkgSbaStatus(loan.sbaLoanStatus ?? '');
    setPkgComments('');
    setPkgDetails(emptyDetails());
    setPkgError(null);
  };

  const closePkgModal = () => { setPkgLoan(null); setPkgError(null); };

  const setDetail = (key: string, val: string | boolean) =>
    setPkgDetails(d => ({ ...d, [key]: val }));


  const handleTypeChange = (t: PackageType | '') => {
    setPkgType(t);
    setPkgDetails(emptyDetails());
  };

  const handleSubmitPkg = async () => {
    if (!pkgLoan || !pkgType) return;
    setPkgSubmitting(true);
    setPkgError(null);
    try {
      const initiator = PACKAGE_META[pkgType].initiator;
      // Only keys valid for this package type (per PACKAGE_FIELD_KEYS) are
      // sent — create-form-only scratch fields (uccSearch, siteVisit, reason,
      // offeror, amountOffered, etc.) are not real PackageFields and are dropped.
      const fields = buildPackageFieldsFromRaw(pkgType, pkgDetails);
      const saChosen = saOfficers.find(m => m.id === pkgSaId);
      const analystChosen = analysts.find(m => m.id === pkgAnalystId);
      await createRequest({
        loanId: pkgLoan.id,
        requestType: pkgType,
        description: pkgComments.trim() || undefined,
        ...(initiator === 'special_assets'
          ? { assignSaOfficerId: saChosen?.id ?? me?.oid, assignSaOfficerName: saChosen?.displayName ?? me?.displayName }
          : { assignAnalystId: analystChosen?.id ?? me?.oid, assignAnalystName: analystChosen?.displayName ?? me?.displayName }),
        sbaLoanStatus: pkgSbaStatus || undefined,
        etranLoanStatus: pkgEtranStatus || undefined,
        fields,
      });
      closePkgModal();
      await load();
    } catch (e) {
      setPkgError(String(e));
    } finally {
      setPkgSubmitting(false);
    }
  };

  const columnIds = saOnly ? SA_COLUMN_IDS : ANALYST_COLUMN_IDS;

  // Filtering and sorting both run entirely in the browser over the full set the
  // endpoint returned — which is what lets derived fields (Line Status, open
  // package) filter and sort exactly like stored ones, with no SQL for them.
  const table = useTableView(
    loans ?? EMPTY_ROWS, columnIds, DEFAULT_SORT, undefined, SORT_TIEBREAK, VIEW_KEY, me?.oid,
  );
  const {
    columns, fields, filters, setFilters, quick, setQuick,
    findAll, setFindAll, toggleSort, resetSort, sortIndicator, sortLabel, view: display,
    effectiveSort, restoredView, restoredIdentity,
  } = table;

  // This table's starting filters — "my assignments" plus Prepurchase hidden,
  // both as chips the user can drop. Which assignment column is targeted follows
  // the role: analyst, or SA rep.
  const defaultFilters = useMemo(
    () => (me ? defaultFiltersFor(me, isAdmin, isAnalyst, isSa) : []),
    [me, isAdmin, isAnalyst, isSa],
  );

  // "Reset to default" returns the whole view to how it opens — the default
  // chip, no search, and the default ordering. Leaving a clicked sort behind
  // would mean the reset only half-worked.
  const resetFilters = () => {
    setFindAll('');
    setFilters(defaultFilters);
    setQuick('');
    resetSort();
  };

  // Searching drops the active filters, the default assignment chip included:
  // its whole purpose is to reach a loan that isn't yours, and leaving that chip
  // in place would filter the answer out of the result it just found.
  const runFind = (term: string) => {
    setFilters([]);
    setQuick('');
    setFindAll(term);
  };

  // The default chip can only be built once `me` has loaded, which is usually
  // after first paint — so seed it on arrival, then only again if the role
  // itself changes (impersonation switches who "my assignments" means). Not on
  // every `defaultFilters` change, or removing the chip would re-add it.
  const seededFor = useRef<string | null>(null);
  // Only defer to a restored view if it was saved by the user now signed in. A view
  // stored under another identity — which impersonation produces — carries an
  // assignment chip naming somebody you are not, so it matches nothing and the
  // filter bar gives no hint why. Those defaults must win. Impersonating also
  // clears stored views outright (AuthProvider); this is the backstop for any path
  // that does not go through the switcher.
  const skipSeed = useRef(restoredView);
  useEffect(() => {
    const identity = me ? `${me.oid}:${columnIds.join()}` : null;
    if (!identity || seededFor.current === identity) return;
    // A restored view already has the user's own filters in it, and `me` arrives
    // after first paint — so seeding here would visibly replace what they left
    // behind with the role defaults. Claim the identity without seeding, once;
    // a later change to it (impersonation switching whose assignments these are)
    // must still re-seed, so this is a one-shot and not a permanent opt-out.
    if (skipSeed.current && (restoredIdentity === undefined || restoredIdentity === me!.oid)) {
      skipSeed.current = false;
      seededFor.current = identity;
      return;
    }
    skipSeed.current = false;
    seededFor.current = identity;
    setFilters(defaultFilters);
    setQuick('');
    setFindAll('');
  }, [me, columnIds, defaultFilters]);

  // Only paint one page of rows at a time — this is what keeps the grid fast at
  // several-thousand-row volumes (rendering every row is the real bottleneck).
  const pageCount = Math.max(1, Math.ceil(display.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = display.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Reset to the first page when the user narrows or reorders — but NOT merely
  // because `display` is a new array. It is rebuilt on every view recompute, so
  // keying off it sent the user back to page 1 each time the background refetch
  // replaced the rows, which is exactly the interruption this page now exists to
  // avoid. Keyed on the inputs that change *what* is shown instead.
  const viewSig = JSON.stringify([filters, quick, findAll, effectiveSort]);
  const sigSeen = useRef(viewSig);
  useEffect(() => {
    // Skip the first run: on mount there is nothing to reset, and a restored
    // page number would be thrown away before it was ever painted.
    if (sigSeen.current === viewSig) return;
    sigSeen.current = viewSig;
    setPage(1);
  }, [viewSig]);

  // Remember where the user is, so returning from a loan lands on these rows.
  // Skips the mount pass for the same reason the table's own write does: an
  // untouched view should leave nothing in storage at all.
  const positionMounted = useRef(false);
  useEffect(() => {
    if (!positionMounted.current) { positionMounted.current = true; return; }
    patchViewState(VIEW_KEY, { page: safePage, expanded: [...expanded] });
  }, [safePage, expanded]);

  // Shared pager, used by both the SA landing and the analyst table.
  const pager = pageCount > 1 ? (
    <div className="pager">
      <button className="btn-sm" disabled={safePage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>← Prev</button>
      <span className="pager-info">
        Page {safePage} of {pageCount} · showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, display.length)} of {display.length}
      </span>
      <button className="btn-sm" disabled={safePage >= pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))}>Next →</button>
    </div>
  ) : null;

  const toggleExpand = (id: number) => {
    setExpanded(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handleRestore = async (id: number) => {
    setActionLoading(id);
    try {
      await restoreLoan(id);
      await load();
    } catch (e) {
      setError(String(e));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="page page-grid">
      <div className="page-header">
        <h1>Purchase &amp; Liquidation Loans</h1>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <FindBar
        active={findAll}
        onSearch={runFind}
        onClear={resetFilters}
        matched={display.length}
        total={loans?.length ?? 0}
      />

      <FilterBar
        rows={loans ?? EMPTY_ROWS}
        fields={fields}
        filters={filters}
        onChange={setFilters}
        quick={quick}
        onQuickChange={setQuick}
        onReset={resetFilters}
        trailing={<>
          {/* "Show deleted" widens what the *server* returns rather than
              narrowing what is loaded, so it stays a toggle, not a chip. */}
          {isAdmin && (
            <label className="checkbox-label">
              <input type="checkbox" checked={includeDeleted} onChange={e => setIncludeDeleted(e.target.checked)} />
              Show deleted
            </label>
          )}
          {/* The opening order is otherwise invisible: the analyst grid sorts by
              Created, which isn't one of its columns, so no header can show it. */}
          {sortLabel && <span className="sort-label">Sorted by {sortLabel}</span>}
          <span className="result-count">
            {loans == null ? '' : (
              display.length === loans.length
                ? `${display.length} record${display.length !== 1 ? 's' : ''}`
                : `${display.length} of ${loans.length} records`
            )}
          </span>
        </>}
      />

      {loans == null ? (
        <p className="loading">Loading…</p>
      ) : display.length === 0 ? (
        <p className="empty">
          {findAll
            ? `No loans anywhere in the portfolio match “${findAll}”. Check the number, or search a borrower name.`
            : table.activeCount > 0
              ? 'No loans match the current filters.'
              : 'No loans to show.'}
        </p>
      ) : (
        <LoansTable
          loans={paged}
          columns={columns}
          onSort={toggleSort}
          indicator={sortIndicator}
          expanded={expanded}
          onToggleExpand={toggleExpand}
          onOpenDetail={id => navigate(`/loans/${id}`)}
          canNewPkg={canNewPkg}
          canRestore={canDelete}
          actionLoading={actionLoading}
          onNewPackage={openPkgModal}
          onRestore={handleRestore}
          pager={pager}
        />
      )}

      {/* ===================================================================
          New Package modal
          =================================================================== */}
      {pkgLoan && (
        <div className="modal-backdrop">
          <div className="modal modal-wide">
            <div className="modal-header">
              <h2>New Package</h2>
              <p className="modal-meta">{pkgLoan.borrowerName} · SBA #{pkgLoan.sbaLoanNumber}{pkgLoan.umwsbLoanNumber ? ` · UMWSB #${pkgLoan.umwsbLoanNumber}` : ''}</p>
            </div>
            <div className="modal-body">
            {pkgError && <div className="error-banner">{pkgError}</div>}
            {memberError && <p className="member-hint">Directory lookup failed ({memberError}); you can still assign yourself.</p>}

            {pkgTypes.length === 0 ? (
              <p className="empty">No package types are available to start for this loan in its current phase.</p>
            ) : (
              <>
                {/* Package type */}
                <label className="detail-field">
                  <span className="field-label">Package Type *</span>
                  <select value={pkgType} onChange={e => handleTypeChange(e.target.value as PackageType | '')}>
                    <option value="">— select —</option>
                    {pkgTypes.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>

                {pkgType && (
                  <>
                    {/* ── Type-specific fields ── */}
                    {pkgType === 'UPP' && (
                      <>
                        <div className="modal-section-label">UPP Details</div>
                        <label className="detail-field">
                          <span className="field-label">Date UPP Submitted</span>
                          <input type="date" value={String(pkgDetails.uppSubmittedDate ?? '')}
                            onChange={e => setDetail('uppSubmittedDate', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Status</span>
                          <select value={String(pkgDetails.uppStatus ?? '')} onChange={e => setDetail('uppStatus', e.target.value)}>
                            <option value="">— select —</option>
                            {UPP_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </label>
                        {pkgDetails.uppStatus === UPP_WITHOUT_CO && (
                          <label className="detail-field">
                            <span className="field-label">Reason Charge Off Not Requested</span>
                            <select value={String(pkgDetails.uppChargeOffNotRequestedReason ?? '')}
                              onChange={e => setDetail('uppChargeOffNotRequestedReason', e.target.value)}>
                              <option value="">— select —</option>
                              {UPP_CO_NOT_REQUESTED_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                          </label>
                        )}
                        {/* With a charge-off, the UPP *is* the charge-off submission (#17), so it
                            carries the same fields — and the CPC claim too when the shape says so. */}
                        {carriesChargeOff('UPP', pkgDetails) && (
                          <>
                            <div className="modal-section-label">Charge Off</div>
                            <label className="detail-field">
                              <span className="field-label">Date Charge Off Submitted</span>
                              <input type="date" value={String(pkgDetails.dateChargeOffSubmitted ?? '')}
                                onChange={e => setDetail('dateChargeOffSubmitted', e.target.value)} />
                            </label>
                            <label className="detail-field">
                              <span className="field-label">Expenses Submitted</span>
                              <input type="number" step="0.01" placeholder="0.00" value={String(pkgDetails.chargeOffExpensesSubmitted ?? '')}
                                onChange={e => setDetail('chargeOffExpensesSubmitted', e.target.value)} />
                            </label>
                          </>
                        )}
                        {String(pkgDetails.uppStatus ?? '').includes('CPC') && (
                          <>
                            <div className="modal-section-label">CPC</div>
                            <label className="detail-field">
                              <span className="field-label">Date CPC Submitted</span>
                              <input type="date" value={String(pkgDetails.cpcSubmittedDate ?? '')}
                                onChange={e => setDetail('cpcSubmittedDate', e.target.value)} />
                            </label>
                            <label className="detail-field">
                              <span className="field-label">Amount of CPC Submitted</span>
                              <input type="number" step="0.01" placeholder="0.00" value={String(pkgDetails.cpcSubmittedAmount ?? '')}
                                onChange={e => setDetail('cpcSubmittedAmount', e.target.value)} />
                            </label>
                          </>
                        )}
                      </>
                    )}


                    {pkgType === 'Purchase' && (
                      <>
                        <div className="modal-section-label">Purchase Request (Special Assets)</div>
                        <label className="detail-field">
                          <span className="field-label">UCC Search Date</span>
                          <input type="date" value={String(pkgDetails.uccSearchDate ?? '')}
                            onChange={e => setDetail('uccSearchDate', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Site Visit Date</span>
                          <input type="date" value={String(pkgDetails.siteVisitDate ?? '')}
                            onChange={e => setDetail('siteVisitDate', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Reason</span>
                          <select value={String(pkgDetails.purchaseReason ?? '')}
                            onChange={e => setDetail('purchaseReason', e.target.value)}>
                            <option value="">— select —</option>
                            {PURCHASE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Date Demand Letter Sent</span>
                          <input type="date" value={String(pkgDetails.dateDemandLetterSent ?? '')}
                            onChange={e => setDetail('dateDemandLetterSent', e.target.value)} />
                        </label>
                      </>
                    )}

                    {pkgType === 'Charge Off' && (
                      <>
                        <div className="modal-section-label">Charge Off Request (Special Assets)</div>
                        <label className="detail-field">
                          <span className="field-label">Collateral Liquidated</span>
                          <select value={pkgDetails.collateralLiquidated === true ? 'yes' : pkgDetails.collateralLiquidated === false ? 'no' : ''}
                            onChange={e => setDetail('collateralLiquidated', e.target.value === '' ? '' : e.target.value === 'yes')}>
                            <option value="">— select —</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Expenses Incurred</span>
                          <select value={pkgDetails.expensesIncurred === true ? 'yes' : pkgDetails.expensesIncurred === false ? 'no' : ''}
                            onChange={e => setDetail('expensesIncurred', e.target.value === '' ? '' : e.target.value === 'yes')}>
                            <option value="">— select —</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Expenses Amount</span>
                          <input type="number" step="0.01" placeholder="0.00" value={String(pkgDetails.expensesAmount ?? '')}
                            onChange={e => setDetail('expensesAmount', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Refer to Treasury</span>
                          <select value={pkgDetails.referToTreasury === true ? 'yes' : pkgDetails.referToTreasury === false ? 'no' : ''}
                            onChange={e => setDetail('referToTreasury', e.target.value === '' ? '' : e.target.value === 'yes')}>
                            <option value="">— select —</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </label>
                      </>
                    )}


                    {pkgType === 'OIC' && (
                      <>
                        <div className="modal-section-label">OIC Request (Special Assets)</div>
                        <label className="detail-field">
                          <span className="field-label">Offeror</span>
                          <input type="text" value={String(pkgDetails.oicOfferor ?? '')}
                            onChange={e => setDetail('oicOfferor', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Amount Offered</span>
                          <input type="number" step="0.01" placeholder="0.00" value={String(pkgDetails.oicAmountOffered ?? '')}
                            onChange={e => setDetail('oicAmountOffered', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">UMWSB Approval Date</span>
                          <input type="date" value={String(pkgDetails.oicUmwsbApprovalDate ?? '')}
                            onChange={e => setDetail('oicUmwsbApprovalDate', e.target.value)} />
                        </label>
                      </>
                    )}

                    {pkgType === 'Lit Plan' && (
                      <>
                        <div className="modal-section-label">Litigation Plan Request (Special Assets)</div>
                        <label className="detail-field">
                          <span className="field-label">Fees &amp; Costs to Date — Approved by SBA</span>
                          <input type="number" step="0.01" placeholder="0.00" value={String(pkgDetails.litFeesApprovedToDate ?? '')}
                            onChange={e => setDetail('litFeesApprovedToDate', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Fees &amp; Costs to Date — Not Approved by SBA</span>
                          <input type="number" step="0.01" placeholder="0.00" value={String(pkgDetails.litFeesNotApprovedToDate ?? '')}
                            onChange={e => setDetail('litFeesNotApprovedToDate', e.target.value)} />
                        </label>
                        {/* Derived, not entered: the two figures above are exhaustive. */}
                        <label className="detail-field">
                          <span className="field-label">Total Fees &amp; Costs to Date</span>
                          <input type="text" readOnly className="readonly" onChange={() => {}}
                            value={litTotalFeesDisplay(pkgDetails)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Total Budget Requested</span>
                          <input type="number" step="0.01" placeholder="0.00" value={String(pkgDetails.litTotalBudgetRequested ?? '')}
                            onChange={e => setDetail('litTotalBudgetRequested', e.target.value)} />
                        </label>
                      </>
                    )}

                    {pkgType === 'Extension' && (
                      <>
                        <div className="modal-section-label">Extension Request (Special Assets)</div>
                        <label className="detail-field">
                          <span className="field-label">Length of Extension</span>
                          <input type="text" placeholder="e.g. 6 months" value={String(pkgDetails.extensionLength ?? '')}
                            onChange={e => setDetail('extensionLength', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Expected Resolution</span>
                          <input type="date" value={String(pkgDetails.extensionExpectedResolution ?? '')}
                            onChange={e => setDetail('extensionExpectedResolution', e.target.value)} />
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Reason</span>
                          <select value={String(pkgDetails.extensionReason ?? '')}
                            onChange={e => setDetail('extensionReason', e.target.value)}>
                            <option value="">— select —</option>
                            {EXTENSION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </label>
                      </>
                    )}

                    {pkgType === 'Return to Regular Servicing' && (
                      <>
                        <div className="modal-section-label">Return to Regular Servicing (Special Assets)</div>
                        <label className="detail-field">
                          <span className="field-label">Modification Completed</span>
                          <select value={pkgDetails.rtsModificationCompleted === true ? 'yes' : pkgDetails.rtsModificationCompleted === false ? 'no' : ''}
                            onChange={e => setDetail('rtsModificationCompleted', e.target.value === '' ? '' : e.target.value === 'yes')}>
                            <option value="">— select —</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </label>
                      </>
                    )}

                    {/* ── Shared fields ── */}
                    <div className="modal-section-label">Shared Information</div>

                    {PACKAGE_META[pkgType].initiator === 'special_assets' ? (
                      <label className="detail-field">
                        <span className="field-label">Special Assets Officer</span>
                        <select value={pkgSaId} onChange={e => setPkgSaId(e.target.value)}>
                          <option value={me?.oid ?? ''}>Myself{me?.displayName ? ` (${me.displayName})` : ''}</option>
                          {saOfficers.filter(m => m.id !== me?.oid).map(m => (
                            <option key={m.id} value={m.id}>{m.displayName}</option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <label className="detail-field">
                        <span className="field-label">Loan Analyst</span>
                        <select value={pkgAnalystId} onChange={e => setPkgAnalystId(e.target.value)}>
                          <option value={me?.oid ?? ''}>Myself{me?.displayName ? ` (${me.displayName})` : ''}</option>
                          {analysts.filter(m => m.id !== me?.oid).map(m => (
                            <option key={m.id} value={m.id}>{m.displayName}</option>
                          ))}
                        </select>
                      </label>
                    )}

                    {/* Analyst-initiated packages (UPP/CPC) set both statuses at create, and
                        so does Purchase — Special Assets records the loan's status when it
                        submits the request (#48). Other SA-initiated types leave them to the
                        analyst's Submit/Decision step (#5). */}
                    {capturesStatusAtCreate(pkgType) && (
                      <>
                        <label className="detail-field">
                          <span className="field-label">eTran Loan Status</span>
                          <select value={pkgEtranStatus} onChange={e => setPkgEtranStatus(e.target.value)}>
                            <option value="">— none —</option>
                            {ETRAN_LOAN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </label>
                        <label className="detail-field">
                          <span className="field-label">Loan Status</span>
                          <select value={pkgSbaStatus} onChange={e => setPkgSbaStatus(e.target.value)}>
                            <option value="">— none —</option>
                            {SBA_LOAN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </label>
                      </>
                    )}

                    <label className="detail-field">
                      <span className="field-label">Comments</span>
                      <textarea rows={3} value={pkgComments} onChange={e => setPkgComments(e.target.value)} />
                    </label>
                  </>
                )}
              </>
            )}
            </div>

            <div className="modal-actions">
              <button className="btn-sm" onClick={closePkgModal} disabled={pkgSubmitting}>Cancel</button>
              <button className="btn-primary" onClick={handleSubmitPkg}
                disabled={pkgSubmitting || !pkgType}>
                {pkgSubmitting ? 'Submitting…' : 'Submit Package'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
